"use client";
import React, { useState, useMemo } from "react";
import { Calendar, dayjsLocalizer, ShowMoreProps } from "react-big-calendar";
import dayjs from "dayjs";
import { ActionIcon, Button, Popover, Text } from "rizzui";
import { PiCaretLeft, PiCaretRight } from "react-icons/pi";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import cn from "../../lib/utils/class-name";
import { isMobile } from "react-device-detect";
import UpDown from "../../assets/upDown";
import YearDropdown from "../../component/yearDropDown";
import SortByProject from "../dashboard/sortProjects";
import GoogleCalendar from "../../assets/googleCalendar";
import MicrosoftIcon from "../../assets/microsoft";
import { useModal } from "../../hooks/useModal";
import SetAvailability from "../../component/setAvailability";
import { useCampaigns } from "../../hooks/useCampaign";
import useAppointments from "../../hooks/useAppointments";
import formatDate from "../../lib/utils/formatDate";
import { useDrawer } from "../../hooks/useDrawer";
import AppointmentDrawer from "../../component/appointmentDrawer";
import MyLoader from "../../component/loader";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

const localizer = dayjsLocalizer(dayjs);

function hexToRgba(hex, alpha = 1) {
  const r = parseInt(hex?.slice(1, 3), 16);
  const g = parseInt(hex?.slice(3, 5), 16);
  const b = parseInt(hex?.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const AdjustAvailability = ({ campaigns, openModal }) => (
  <Popover>
    <Popover.Trigger>
      <Button className="rounded-[4px] size-[14px] w-fit px-[14px] py-[5px] md:py-[9px] h-fit border-primary-dark border !bg-background">
        <Text className="font-switzer font-[500] mr-[4px] text-[12px] text-primary-dark">
          Adjust your availability
        </Text>
        <UpDown className={"text-primary-dark size-[14px] md:size-[16px]"} />
      </Button>
    </Popover.Trigger>
    <Popover.Content className="w-[220px] dark:bg-[#2A2C30] rounded-[8px] !px-[8px] !py-[8px]">
      <div>
        {campaigns?.map((campaign) => (
          <p
            className="h-[44px] p-[16px] w-full cursor-pointer font-switzer text-[14px] text-primary-dark font-[400] flex items-center hover:dark:!bg-[#303236] hover:bg-gray-100"
            key={campaign?._id}
            onClick={() =>
              openModal({
                view: (
                  <SetAvailability page="appointments" id={campaign?._id} />
                ),
              })
            }
          >
            {campaign?.campaign_name}
          </p>
        ))}
      </div>
    </Popover.Content>
  </Popover>
);

interface ToolbarProps {
  date: Date;
  setDate: (date: Date) => void;
  handleMonth: (nav: "next" | "previous") => void;
  handleYear: (newYear: number) => void;
}

const Toolbar = React.memo(
  ({ date, setDate, handleMonth, handleYear }: ToolbarProps) => {
    const { data, isLoading } = useCampaigns();
    const months = useMemo(
      () => [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      []
    );
    return (
      <>
        <div className="flex flex-row flex-wrap gap-[24px] mb-[23px]">
          <div className="z-50 col-span-1 flex flex-row justify-between">
            <SortByProject />
          </div>
          <div className="flex ml-auto flex-row gap-[8px] items-center justify-stretch md:order-4">
            <div className="size-[24px] bg-[#CEE7FF] rounded-full flex justify-center items-center">
              <GoogleCalendar className="size-[12px]" />
            </div>
            <div className="size-[24px] bg-[#CEE7FF] rounded-full flex justify-center items-center">
              <MicrosoftIcon className="size-[12px]" />
            </div>
          </div>

          <div className="md:order-5">
            <AdjustAvailability
              campaigns={data}
              openModal={useModal().openModal}
            />
          </div>
          <div className="flex flex-row gap-[10px] ml-auto md:ml-0">
            <ActionIcon
              className="h-[30px] w-[58px] md:h-[35px] md:w-[63px] rounded-[4px] border border-primary-dark bg-background font-switzer font-[500] text-[12px] leading-[15.84px] text-primary-dark hover:text-primary-dark hover:!bg-background"
              onClick={() => setDate(new Date())}
            >
              <p>Today</p>
            </ActionIcon>
            <div className="flex flex-row gap-[6px]">
              <ActionIcon
                className="bg-btn hover:!bg-btn size-[30px] md:size-[35px] rounded-[4px]"
                onClick={() => handleMonth("previous")}
              >
                <PiCaretLeft size={24} />
              </ActionIcon>
              <ActionIcon
                className="bg-btn hover:!bg-btn size-[30px] md:size-[35px] rounded-[4px]"
                onClick={() => handleMonth("next")}
              >
                <PiCaretRight size={24} />
              </ActionIcon>
            </div>
          </div>

          <div className="flex flex-row mx-auto">
            <p className="text-primary-dark font-[600] font-ibmPlexSerif text-[18px] md:text-[24px] leading-[31.2px]">
              {months[date.getMonth()]}{" "}
              <span className="font-[400]">{date.getFullYear()}</span>
            </p>
            <div className="text-primary-dark flex self-center">
              <Popover>
                <Popover.Trigger>
                  <ActionIcon className="!bg-transparent hover:!bg-transparent size-[18px] p-0">
                    <UpDown className="text-primary-dark" />
                  </ActionIcon>
                </Popover.Trigger>
                <Popover.Content className="p-0">
                  <YearDropdown
                    date={date.getFullYear()}
                    handleDate={handleYear}
                    setIsOpen={() => {}}
                  />
                </Popover.Content>
              </Popover>
            </div>
          </div>
        </div>
      </>
    );
  }
);

interface EventProps {
  event: {
    id: number;
    title: string;
  };
}

const Event = ({ event }: EventProps) => {
  const { openDrawer } = useDrawer();
  const eventColors = useMemo(
    () => [
      { badge: "#1A98D4", background: "#1A98D4" },
      { badge: "#8A2BE2", background: "#8A2BE2" },
      { badge: "#003D73", background: "#003D73" },
    ],
    []
  );
  const backgroundColor =
    eventColors[event.id % eventColors.length]?.background ||
    eventColors[0].background;
  return (
    <div
      onClick={() =>
        openDrawer({
          view: <AppointmentDrawer event={event} />,
          placement: "right",
          containerClassName: "w-full md:!w-[435px]",
        })
      }
      className={cn(
        "text-primary-dark font-[500] rounded-s-[2px] !bg-opacity-40 text-[12px] leading-[15.84px] font-switzer h-[25px] place-items-center flex-row flex !w-full !overflow-hidden justify-start"
      )}
      style={{ backgroundColor: hexToRgba(backgroundColor, 0.4) }}
    >
      <div
        className={cn("w-[5px] h-full")}
        style={{ backgroundColor: hexToRgba(backgroundColor, 1) }}
      />
      <p className="ml-[6px] truncate whitespace-nowrap overflow-hidden">
        {event.title}
      </p>
    </div>
  );
};

const ShowMore = ({ count, remainingEvents }: ShowMoreProps) => {
  const eventColors = useMemo(
    () => [
      { badge: "#1A98D4", background: "#1A98D4" },
      { badge: "#8A2BE2", background: "#8A2BE2" },
      { badge: "#003D73", background: "#003D73" },
    ],
    []
  );
  const backgroundColor =
    eventColors[count % eventColors.length]?.background ||
    eventColors[0].background;
  const { openDrawer } = useDrawer();
  return (
    <Popover>
      <Popover.Trigger>
        <div
          className={cn(
            "text-primary-dark font-[500] rounded-s-[2px] !bg-opacity-40 text-[12px] leading-[15.84px] font-switzer h-[25px] place-items-center flex-row flex !w-full !overflow-hidden justify-start cursor-pointer"
          )}
          style={{ backgroundColor: hexToRgba(backgroundColor, 0.4) }}
        >
          <div
            className={cn("w-[5px] h-full")}
            style={{ backgroundColor: hexToRgba(backgroundColor, 1) }}
          />
          <p className="ml-[6px] truncate whitespace-nowrap overflow-hidden">
            {count} other appointments
          </p>
        </div>
      </Popover.Trigger>
      <Popover.Content
        className={
          "bg-background dark:bg-[#2A2C30] w-[calc((100%/7)-62px)] border-muted border customBar max-h-[200px] h-auto overflow-y-auto flex flex-col gap-[2px] !px-0 !py-2"
        }
      >
        {remainingEvents?.map((entry) => (
          <div
            onClick={() =>
              openDrawer({
                view: <AppointmentDrawer event={entry} />,
                placement: "right",
                containerClassName: "w-full md:!w-[435px]",
              })
            }
            className={cn(
              "text-primary-dark font-[500] rounded-s-[2px] text-[12px] leading-[15.84px] font-switzer h-[25px] place-items-center flex-row flex !w-full !overflow-hidden justify-start cursor-pointer hover:dark:!bg-[#303236] hover:bg-gray-100 py-4"
            )}
            //style={{ backgroundColor: hexToRgba(backgroundColor, 0.4) }}
          >
            <div
              className={cn("w-[5px] h-full")}
              //style={{ backgroundColor: hexToRgba(backgroundColor, 1) }}
            />
            <p className="ml-[6px] truncate whitespace-nowrap overflow-hidden w-[calc(100%-20px)]">
              {entry.title}
            </p>
          </div>
        ))}
      </Popover.Content>
    </Popover>
  );
};

const MyCalendar = () => {
  const [date, setDate] = useState(new Date());

  const { isLoading, data: appointments } = useAppointments();

  const events = appointments?.flat().map((entry: any, index) => ({
    id: index,
    call_id: entry?.call_id,
    title: `${formatDate(entry?.start)
      .split("|")[1]
      .replace(":", "")
      .replaceAll("0", "")
      .toLowerCase()} ${entry?.lead?.firstname ?? ""} ${
      entry?.lead?.lastname ?? ""
    }`,
    start: new Date(entry?.start),
    end: new Date(entry?.end),
    lead_details: entry?.lead,
    campaign: entry?.campaign,
    data: entry,
  }));

  const handleMonth = (nav: "next" | "previous") => {
    const newDate = new Date(date);
    newDate.setMonth(
      nav === "next" ? date.getMonth() + 1 : date.getMonth() - 1
    );
    setDate(newDate);
  };

  const handleYear = (newYear: number) => {
    const newDate = new Date(date);
    newDate.setFullYear(newYear);
    setDate(newDate);
  };

  return (
    <div className="h-full">
      {isLoading ? (
        <MyLoader />
      ) : (
        <Calendar
          localizer={localizer}
          views={["month"]}
          date={date}
          popup
          events={events ?? []}
          culture=""
          formats={{
            dateFormat: "D",
            weekdayFormat: (date, culture, localizer) =>
              localizer!.format(date, isMobile ? "ddd" : "dddd", culture),
          }}
          className="font-switzer font-[500] text-[14px] leading-[18.48px] !h-[508px] md:!h-[608px] text-primary-dark"
          components={{
            toolbar: (props) => (
              <Toolbar
                {...props}
                date={date}
                setDate={setDate}
                handleMonth={handleMonth}
                handleYear={handleYear}
              />
            ),
            event: Event,
            showMore: ShowMore,
          }}
          onNavigate={setDate}
        />
      )}
    </div>
  );
};

export default function Appointment() {
  return <MyCalendar />;
}
